import { Form } from 'antd';
import { useTestSourceConnectionMutation } from '../services/api';
import type { TestConnectionRequest } from '../types';
import type { ConnectionTestResult } from '../components/ConfigurationForms/types';

export function useConnectionTest<TConfig>(
    transformer: (config: TConfig) => TestConnectionRequest,
    validator: (config: TConfig | null) => boolean,
    onSuccess?: () => void
) {
    const form = Form.useFormInstance();
    const [testConnection, { isLoading }] = useTestSourceConnectionMutation();

    /**
     * Retrieves and validates the current configuration from the form
     * @returns The validated config object or null if validation fails
     */
    const getConfig = (): TConfig | null => {
        const values = form.getFieldsValue(['configuration']);
        const config = values.configuration as TConfig;

        if (!validator(config)) {
            return null;
        }

        return config;
    };

    /**
     * Tests the connection using the provided configuration
     * Calls onSuccess callback if test passes
     * @param config - The configuration to test
     * @returns The connection test result
     */
    const handleTestConnection = async (config: TConfig): Promise<ConnectionTestResult> => {
        const result = await testConnection(transformer(config)).unwrap();

        if (result.success && onSuccess) {
            onSuccess();
        }

        return result;
    };

    return {
        getConfig,
        handleTestConnection,
        isLoading,
    };
}
